import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { CardContent } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import AppointmentService from '../../../../../../services/appointment/appointment-service';
import { PatientAppointment } from '../../../../../../services/appointment/types';
import { ProceduresDialog } from '../../../../../layout/slideout/patient-profile-widget/views/visits/procedures-dialog/procedures-dialog';
import { PastAppointmentItem } from '../../../../../shared/appointment/past-appointment-item';
import { UpcomingAppointmentItem } from '../../../../../shared/appointment/upcoming-appointment-item';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { usePatientProfilePage } from '../../hooks';
import { PatientProfileRoutePath } from '../../navigation/route-config';
import { Card, CardTitle } from '../../patient-profile-page.styles';
import { VisitList } from './overview.styles';
import { VisitsSkeleton } from './visits-skeleton';

export default function Visits() {
  const { goTo } = usePatientProfilePage();
  const { contactDetails } = usePatientProfile();
  const [isLoading, setIsLoading] = useState(false);

  const [appointments, setAppointments] = useState<PatientAppointment[]>([]);

  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { showError } = useNotification();

  const [selectedItem, setSelectedItem] = useState<PatientAppointment>();
  const [dialogOpened, setDialogOpened] = useState(false);

  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  const loadAppointments = useCallback(async () => {
    try {
      if (!contactDetails.id || !contactDetails.location?.id) return;

      setIsLoading(true);
      const appts = await appointmentService.getPatientAppointments(contactDetails.id, contactDetails.location?.id);
      setAppointments(appts.sort((a, b) => moment(b.date).diff(moment(a.date))).slice(0, 4));
    } catch (e: any) {
      if (e?.status !== 404) showError('Unable to load appointments');
    } finally {
      setIsLoading(false);
    }
  }, [appointmentService, contactDetails.id, contactDetails.location?.id]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  const handleItemClick = (appointment: PatientAppointment) => {
    setSelectedItem(appointment);
    setDialogOpened(true);
  };

  const handleDialogClose = async () => {
    setDialogOpened(false);
    setSelectedItem(undefined);
  };

  const upcomingAppointmentItem = (appointment: PatientAppointment) => {
    return (
      <UpcomingAppointmentItem
        key={appointment.id}
        appointment={appointment}
        onClick={() => handleItemClick(appointment)}
      />
    );
  };

  const pastAppointmentItem = (appointment: PatientAppointment) => {
    return (
      <PastAppointmentItem
        key={appointment.id}
        appointment={appointment}
        onClick={() => handleItemClick(appointment)}
      />
    );
  };

  return (
    <>
      <Card sx={{ marginTop: '16px' }}>
        <CardContent>
          <CardTitle className="clickable" onClick={() => goTo(PatientProfileRoutePath.VISITS)}>
            Visits
            <ArrowForwardIosIcon />
          </CardTitle>
          {isLoading && (
            <VisitList>
              <VisitsSkeleton />
            </VisitList>
          )}
          {!isLoading && appointments.length === 0 && <NoDataComponent />}
          {!isLoading && appointments.length > 0 && (
            <VisitList>
              {appointments.map((appt) => {
                return moment(appt.date).isBefore(moment()) ? pastAppointmentItem(appt) : upcomingAppointmentItem(appt);
              })}
            </VisitList>
          )}
        </CardContent>
      </Card>
      {selectedItem && (
        <ProceduresDialog isOpen={dialogOpened} appointment={selectedItem} onClose={handleDialogClose} />
      )}
    </>
  );
}
